/**
 * Step 4 (Pitchers) of the Build Itinerary wizard.
 *
 * Lets the coach mark which pitcher throws in which game. The candidates for a game are the
 * followed players on its two teams, plus anyone already marked so a saved mark never drops out of
 * view. Only games with at least one candidate are listed, grouped by day. A game can have several
 * pitchers. Marks are kept as a local draft and saved in one request when the coach presses Next,
 * sending only the games that changed.
 */

import { useMemo, useState } from 'react';
import { formatEventClockTime, timeZoneAbbrev } from '../../../../../utils/calendarTime';
import { setGamesPitchers } from '../../../../../services/tournament-games';
import type { GamePitchersEntry } from '../../../../../types/tournament-games';
import { getSafeErrorMessage } from '../../../../../utils/errorHandler';
import { Checkbox } from '../../../../../components/Checkbox';
import { useTournamentGames } from './useTournamentGames';
import { WizardFooter } from './WizardFooter';
import { formatDayHeading, groupGamesByDay } from './itinerary';
import { pitcherCandidates, pitcherIds, sameIdSet, toggleId } from '../gamePitchers';
import { tourAnchor } from '../../../../tours/tourAnchors';
import { useTour } from '../../../../tours/useTour';
import { BUILD_ITINERARY_FEATURE, BUILD_ITINERARY_TOURS } from './buildItineraryTours';

interface PitchersStepProps {
  /** The confirmed tournament. Absent until Step 1 imports + confirms. */
  calendarEventId: string | undefined;
  onNext: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
}

export function PitchersStep({ calendarEventId, onNext, onBack, canGoBack }: PitchersStepProps) {
  const gamesState = useTournamentGames(calendarEventId);
  // Only games the coach has touched; anything absent reads its saved pitchers.
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useTour(BUILD_ITINERARY_FEATURE, {
    ready: Boolean(calendarEventId) && gamesState.status === 'ready',
    hasData: false,
    pageTourKey: BUILD_ITINERARY_TOURS.pitchers,
    dataTourKey: null,
  });

  const games = useMemo(
    () => (gamesState.status === 'ready' ? gamesState.games : []),
    [gamesState],
  );

  const dayGroups = useMemo(
    () =>
      groupGamesByDay(
        games
          .filter((game) => pitcherCandidates(game).length > 0)
          .map((game) => ({ game, matchedSelectedPlayers: [] })),
      ),
    [games],
  );

  if (!calendarEventId) {
    return (
      <div className="wiz-error-box" data-testid="pitchers-no-tournament">
        <p>
          The imported schedule has not been confirmed into a tournament yet. Finish importing on
          the Event step first.
        </p>
      </div>
    );
  }

  if (gamesState.status === 'loading' || gamesState.status === 'idle') {
    return (
      <div className="wiz-players-state" data-testid="pitchers-loading">
        <span className="wiz-spinner" aria-hidden="true" />
        <span>Loading games...</span>
      </div>
    );
  }

  if (gamesState.status === 'error') {
    return (
      <div className="wiz-error-box" data-testid="pitchers-error">
        <p role="alert">{gamesState.message}</p>
      </div>
    );
  }

  const selectedFor = (gameId: string, saved: string[]) => draft[gameId] ?? saved;

  const handleToggle = (gameId: string, saved: string[], prospectId: string, on: boolean) => {
    setDraft((prev) => ({
      ...prev,
      [gameId]: toggleId(prev[gameId] ?? saved, prospectId, on),
    }));
  };

  const handleNext = async () => {
    if (saving) return;
    const changed: GamePitchersEntry[] = games
      .filter((game) => draft[game.id] && !sameIdSet(draft[game.id], pitcherIds(game)))
      .map((game) => ({ gameId: game.id, prospectIds: draft[game.id] }));
    if (changed.length === 0) {
      onNext();
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await setGamesPitchers(calendarEventId, changed);
      onNext();
    } catch (err) {
      setSaveError(getSafeErrorMessage(err, "Couldn't save pitchers. Try again."));
      setSaving(false);
    }
  };

  return (
    <div data-testid="pitchers-step">
      <h2 className="wiz-step-title">Step 4: Pitchers</h2>
      <p className="wiz-sub">
        Check who is pitching in each game. You can pick more than one pitcher for a game. Only
        games with players you follow are listed.
      </p>

      <WizardFooter
        onBack={onBack}
        canGoBack={canGoBack}
        legend="You can change pitchers later from the game list."
      >
        <button
          type="button"
          className="wiz-btn wiz-btn--primary"
          onClick={handleNext}
          disabled={saving}
          data-testid="pitchers-next"
          {...tourAnchor('build-itinerary.pitchers.next')}
        >
          {saving ? 'Saving...' : 'Next: Assign to Staff →'}
        </button>
      </WizardFooter>

      {saveError && (
        <div className="wiz-error-box" data-testid="pitchers-save-error">
          <p role="alert">{saveError}</p>
        </div>
      )}

      {dayGroups.length === 0 ? (
        <div
          className="wiz-card wiz-card--pad"
          data-testid="pitchers-empty"
          {...tourAnchor('build-itinerary.pitchers.list')}
        >
          <p className="wiz-sub">
            None of the pitchers you follow are in these games. Follow a pitcher in the last step to
            pick them here, or press Next to go on.
          </p>
        </div>
      ) : (
        <div className="wiz-card" data-testid="pitchers-list" {...tourAnchor('build-itinerary.pitchers.list')}>
          {dayGroups.map((group) => (
            <div key={group.dayKey}>
              <div className="wiz-daygroup">{formatDayHeading(group.dayKey)}</div>
              <ul className="wiz-pitchers">
                {group.games.map(({ game }) => {
                  const saved = pitcherIds(game);
                  const selected = selectedFor(game.id, saved);
                  const tz = timeZoneAbbrev(game.timeZone, new Date(game.startsAtUtc));
                  const headingId = `pitchers-game-${game.id}`;
                  return (
                    <li
                      key={game.id}
                      className="wiz-pitchers__game"
                      data-testid={`pitchers-game-${game.id}`}
                    >
                      <div className="wiz-pitchers__head" id={headingId}>
                        <span className="wiz-pitchers__time">
                          {formatEventClockTime(game)}
                          {tz && ` ${tz}`}
                        </span>{' '}
                        <span className="wiz-pitchers__matchup">
                          {game.homeTeamNameRaw} vs {game.awayTeamNameRaw}
                        </span>
                        {game.fieldName && (
                          <span className="wiz-pitchers__field"> · {game.fieldName}</span>
                        )}
                      </div>
                      <div
                        className="wiz-pitchers__options"
                        role="group"
                        aria-labelledby={headingId}
                      >
                        {pitcherCandidates(game).map((p) => (
                          <Checkbox
                            key={p.prospectId}
                            label={p.name}
                            checked={selected.includes(p.prospectId)}
                            disabled={saving}
                            onChange={(e) =>
                              handleToggle(game.id, saved, p.prospectId, e.target.checked)
                            }
                          />
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
